import { useEffect, useState } from "react";
import { ArrowUpRight, Users, BedDouble } from "lucide-react";
import { Link } from "react-router-dom";
import { PROPERTIES, SITE } from "../../data/content";

const COTTAGE_IDS = ["lock-keepers-cottage", "acton-bridge-cottage", "oakmere-cottage"];
const HUT_IDS = ["shepherds-hut-dutton", "oakmere", "the-hide"];

function CardImage({ p }) {
  const images = Array.isArray(p.images) && p.images.length > 1 ? p.images : [p.image];
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % images.length);
    }, 5000);
    return () => clearInterval(id);
  }, [images.length]);

  return (
    <div className="relative media-zoom aspect-[4/3] w-full overflow-hidden">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`${p.name} — ${p.type}, Cheshire holiday let`}
          loading="lazy"
          data-testid={`property-image-${p.id}-${i}`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: i === active ? 1 : 0,
            transition: "opacity 1.4s ease-in-out",
          }}
        />
      ))}
      {p.badge && (
        <span className="property-badge" data-testid={`property-badge-${p.id}`}>
          {p.badge}
        </span>
      )}
    </div>
  );
}

function PropertyCard({ p, index }) {
  return (
    <article
      data-testid={`property-card-${p.id}`}
      className="property-card fade-up"
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <CardImage p={p} />
      <div className="p-7 sm:p-8 flex flex-col gap-5 flex-1">
        <div>
          <p className="overline" data-testid={`property-type-${p.id}`}>{p.type}</p>
          <h3
            data-testid={`property-name-${p.id}`}
            className="mt-3 font-serif text-3xl sm:text-[2rem] tracking-tight"
            style={{ color: "var(--dl-ink)", fontWeight: 500 }}
          >
            {p.name}
          </h3>
        </div>

        <div className="flex items-center gap-5 text-sm" style={{ color: "var(--dl-ink-2)" }}>
          <span className="inline-flex items-center gap-2">
            <Users size={14} /> Sleeps {p.sleeps}
          </span>
          <span className="inline-flex items-center gap-2">
            <BedDouble size={14} /> {p.bedrooms} {p.bedrooms === 1 ? "bedroom" : "bedrooms"}
          </span>
        </div>

        <p className="text-[0.95rem] leading-relaxed" style={{ color: "var(--dl-ink-2)" }}>
          {p.description}
        </p>

        <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pt-1" style={{ color: "var(--dl-ink)" }}>
          {p.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2">
              <span
                className="inline-block w-1.5 h-1.5 mt-2"
                style={{ background: "var(--dl-accent)" }}
              />
              {h}
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-4 flex flex-col gap-3">
          <div>
            <a
              data-testid={`property-reserve-btn-${p.id}`}
              href={SITE.bookingExternal}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
            >
              Reserve Now <ArrowUpRight size={16} />
            </a>
          </div>
          {(p.tourUrl || p.galleryRoute) && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {p.tourUrl && (
                <a
                  data-testid={`property-tour-link-${p.id}`}
                  href={p.tourUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                  style={{ borderBottomColor: "var(--dl-accent)", color: "var(--dl-accent)" }}
                >
                  Take a Walkthrough Tour <ArrowUpRight size={16} />
                </a>
              )}
              {p.galleryRoute && (
                <Link
                  data-testid={`property-gallery-link-${p.id}`}
                  to={p.galleryRoute}
                  className="btn-ghost"
                  style={{ borderBottomColor: "var(--dl-accent)", color: "var(--dl-accent)" }}
                >
                  View Gallery <ArrowUpRight size={16} />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function PropertyGrid({ ids }) {
  const items = PROPERTIES.filter((p) => ids.includes(p.id));
  return (
    <div className="grid gap-8 sm:gap-10 md:grid-cols-2 xl:grid-cols-3">
      {items.map((p, i) => (
        <PropertyCard key={p.id} p={p} index={i} />
      ))}
    </div>
  );
}

export default function Properties() {
  return (
    <>
      <section
        id="cottages"
        data-testid="cottages-section"
        className="py-24 sm:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <p className="section-eyebrow">
                <span className="divider-rule" /> The Cottages
              </p>
              <h2
                className="mt-5 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]"
                style={{ color: "var(--dl-ink)", fontWeight: 400 }}
              >
                Two Victorian cottages,
                <br />
                <span className="serif-italic">honest country comfort.</span>
              </h2>
            </div>
            <p className="max-w-md text-base" style={{ color: "var(--dl-ink-2)" }}>
              Fully furnished and ready for short breaks, holidays or longer stays.
              Each cottage sleeps up to four guests across two bedrooms.
            </p>
          </div>
          <PropertyGrid ids={COTTAGE_IDS} />
        </div>
      </section>

      <section
        id="shepherds-huts"
        data-testid="shepherds-huts-section"
        className="py-24 sm:py-32"
        style={{ background: "var(--dl-bg-2)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <p className="section-eyebrow">
                <span className="divider-rule" /> Shepherd&apos;s Huts &amp; Lodge
              </p>
              <h2
                className="mt-5 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]"
                style={{ color: "var(--dl-ink)", fontWeight: 400 }}
              >
                Hand-built retreats with
                <br />
                <span className="serif-italic">wood-fired hot tubs.</span>
              </h2>
            </div>
            <p className="max-w-md text-base" style={{ color: "var(--dl-ink-2)" }}>
              We design and build our own shepherd&apos;s huts. Each sits in its own
              landscaped private garden — on a river island, by a private mere,
              or on the edge of a lake.
            </p>
          </div>
          <PropertyGrid ids={HUT_IDS} />
        </div>
      </section>
    </>
  );
}
